import { Point } from '../primitives/point';
import { Rect } from '../primitives/rect';
import { intersect3, cloneBlazorObject } from '../utility/diagram-util';
import { cloneObject } from '../utility/base-util';
import { DiagramEvent, ConnectorConstraints } from './../enum/enum';
import { contains } from './actions';
import { getOppositeDirection } from '../utility/connector';
import { StraightSegment, OrthogonalSegment } from '../objects/connector';
import { ToolBase } from './tool';
import { isBlazor } from '@syncfusion/ej2-base';
/**
 * Multiple segments editing for Connector
 */
export class ConnectorEditing extends ToolBase {
    constructor(commandHandler, endPoint) {
        super(commandHandler, true);
        this.oldValue = null;
        this.endPoint = endPoint;
    }
    /**
     * mouseDown method\
     *
     * @returns {  void }    mouseDown method .\
     * @param {MouseEventArgs} args - provide the args value.
     * @private
     */
    mouseDown(args) {
        let connectors;
        let edit = true;
        if (args.source && args.source.connectors) {
            connectors = args.source.connectors[0];
        }
        if (args.info && args.actualObject) {
            edit = args.info.ctrlKey && args.actualObject.type !== 'Orthogonal';
        }
        if (connectors && edit) {
            this.inAction = true;
            this.undoElement = cloneObject(args.source);
            super.mouseDown(args);
            // 927583: Segment points cannot be dragged when the pointer is in the outer part of the segmentThumb
            const inheritSegmentThumbSize = (connectors.constraints & ConnectorConstraints.InheritSegmentThumbSize);
            const segmentThumbSize = inheritSegmentThumbSize ?
                this.commandHandler.diagram.segmentThumbSize : connectors.segmentThumbSize;
            let padding = (segmentThumbSize > 20) && connectors.type !== 'Straight' ? segmentThumbSize / 2 : 10;
            padding = padding / this.commandHandler.diagram.scrollSettings.currentZoom;
            // Sets the selected segment
            for (let i = 0; i < connectors.segments.length; i++) {
                const segment = connectors.segments[parseInt(i.toString(), 10)];
                if (this.endPoint === 'OrthoThumb') {
                    for (let j = 0; j < segment.points.length - 1; j++) {
                        const segPoint = { x: 0, y: 0 };
                        segPoint.x = ((segment.points[parseInt(j.toString(), 10)].x + segment.points[j + 1].x) / 2);
                        segPoint.y = ((segment.points[parseInt(j.toString(), 10)].y + segment.points[j + 1].y) / 2);
                        if (contains(this.currentPosition, segPoint, padding)) {
                            this.selectedSegment = segment;
                            this.segmentIndex = j;
                        }
                    }
                }
                else {
                    if (contains(this.currentPosition, segment.point, padding)) {
                        this.selectedSegment = segment;
                    }
                }
            }
        }
    }
    /**
     * mouseMove method\
     *
     * @returns {  void }    mouseMove method .\
     * @param {MouseEventArgs} args - provide the args value.
     * @private
     */
    mouseMove(args) {
        super.mouseMove(args);
        this.currentPosition = args.position;
        if (this.currentPosition && this.prevPosition) {
            const diffY = this.currentPosition.y - this.prevPosition.y;
            const diffX = this.currentPosition.x - this.prevPosition.x;
            this.currentPosition = this.commandHandler.snapConnectorEnd(this.currentPosition);
            let connector;
            let newValue;
            let isSame = false;
            if (args.source && args.source.connectors) {
                connector = args.source.connectors[0];
            }
            if (Point.equals(this.startPosition, this.prevPosition)) {
                isSame = true;
            }
            if (this.oldValue === null) {
                this.oldValue = cloneObject(this.selectedSegment);
            }
            let arg = {
                source: connector, state: 'Start', oldValue: this.oldValue, newValue: this.oldValue,
                segment: this.selectedSegment, cancel: false
            };
            //EJ2-66217 - Support to add event for segment points editing in connector.
            arg = {
                source: cloneBlazorObject(connector), state: 'Start', oldValue: cloneBlazorObject(this.oldValue),
                newValue: cloneBlazorObject(this.oldValue),
                segment: cloneBlazorObject(this.selectedSegment), cancel: false
            };
            if (isSame && !isBlazor()) {
                this.commandHandler.triggerEvent(DiagramEvent.segmentChange, arg);
            }
            // When cancel is set to true at state Start, the segment change will be prevented.
            if (arg.cancel) {
                this.commandHandler.diagram.resetTool();
            }
            if ((this.inAction && this.selectedSegment !== undefined && this.endPoint !== undefined)
                && (diffX !== 0 || diffY !== 0) && !arg.cancel) {
                if (this.endPoint === 'OrthoThumb') {
                    this.blocked = !this.dragOrthogonalSegment(connector, this.selectedSegment, this.currentPosition, this.segmentIndex);
                    if (this.oldValue.length === null && this.oldValue.direction === null) {
                        this.oldValue = cloneObject(this.selectedSegment);
                    }
                    newValue = cloneObject(this.selectedSegment);
                }
                else {
                    const tx = this.currentPosition.x - this.selectedSegment.point.x;
                    const ty = this.currentPosition.y - this.selectedSegment.point.y;
                    const index = connector.segments.indexOf(this.selectedSegment);
                    this.blocked = !this.commandHandler.dragControlPoint(connector, tx, ty, false, index);
                    newValue = cloneObject(this.selectedSegment);
                }
                this.commandHandler.updateSelector();
                this.currentPosition = args.position;
                const arg1 = {
                    source: cloneBlazorObject(connector), state: 'Progress', oldValue: cloneBlazorObject(this.oldValue),
                    newValue: cloneBlazorObject(newValue),
                    segment: cloneBlazorObject(this.selectedSegment), cancel: false
                };
                this.commandHandler.triggerEvent(DiagramEvent.segmentChange, arg1);
            }
        }
        this.prevPosition = this.currentPosition;
        return !this.blocked;
    }
    /**
     * mouseUp method\
     *
     * @returns {  void }    mouseUp method .\
     * @param {MouseEventArgs} args - provide the args value.
     * @private
     */
    mouseUp(args) {
        let connector;
        let oldValues;
        if (args.source && args.source.connectors) {
            connector = args.source.connectors[0];
            oldValues = { segments: connector.segments };
        }
        if (args && args.source && args.info && args.info.ctrlKey && args.info.shiftKey && connector.type === 'Straight') {
            this.addOrRemoveSegment(connector, this.currentPosition);
        }
        else {
            if (this.endPoint === 'OrthoThumb' && this.selectedSegment) {
                const index = connector.segments.indexOf(this.selectedSegment);
                const prev = connector.segments[index - 1];
                const next = connector.segments[index + 1];
                if (index === connector.segments.length - 2
                    && this.updateLastSegment(connector, this.selectedSegment)) {
                    connector.segments.splice(connector.segments.length - 2, 1);
                }
                else {
                    //Bug 853404: Exception occurs while adjusting the connector segment.
                    if (prev && Math.abs(prev.length) < 5 && prev.length > 0) {
                        if (index !== 1) {
                            this.removePrevSegment(connector, index);
                        }
                    }
                    else if (next) {
                        const len = Point.distancePoints(next.points[0], next.points[1]);
                        const length = ((next.length || next.length === 0) ? next.length : len);
                        if ((Math.abs(length) <= 5)) {
                            this.removeNextSegment(connector, index);
                        }
                    }
                }
                this.commandHandler.updateEndPoint(connector, oldValues);
                // EJ2-65063 - While mouseUp set the selectedSegmentIndex as zero.
                connector.selectedSegmentIndex = 0;
            }
        }
        if (this.undoElement) {
            //let obj: SelectorModel;
            const obj = cloneObject(args.source);
            const entry = {
                type: 'SegmentChanged', redoObject: obj, undoObject: this.undoElement, category: 'Internal'
            };
            this.commandHandler.addHistoryEntry(entry);
        }
        if (connector.isBezierEditing) {
            connector.isBezierEditing = false;
        }
        if (this.prevPosition !== this.startPosition) {
            const arg = {
                source: cloneBlazorObject(connector), state: 'Completed', oldValue: cloneBlazorObject(this.oldValue),
                newValue: cloneObject(this.selectedSegment), cancel: false, segment: cloneBlazorObject(this.selectedSegment)
            };
            this.commandHandler.triggerEvent(DiagramEvent.segmentChange, arg);
        }
        super.mouseUp(args);
    }
    removePrevSegment(connector, index) {
        const first = connector.segments[index - 2];
        const next = connector.segments[index + 1];
        //Bug 905092: Exception through while dragging segment and reverted back to its original position.
        //Added next condition to check the next segment is not null.
        if (next) {
            const length = (next.length || next.length === 0) ? next.length : Point.distancePoints(next.points[0], next.points[1]);
            if (!(length <= 5)) {
                const removeSegments = connector.segments.slice(index - 1, index + 1);
                const args = {
                    element: connector, removeSegments: removeSegments, type: 'Removal', cancel: false
                };
                //Removed isBlazor code
                this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
                if (!args.cancel) {
                    const last = connector.segments[index + 1];
                    connector.segments.splice(index - 1, 2);
                    const segment = this.selectedSegment;
                    if (segment.direction === 'Left' || segment.direction === 'Right') {
                        first.points[first.points.length - 1].x = last.points[0].x;
                        last.points[0].y = first.points[first.points.length - 1].y;
                    }
                    else {
                        first.points[first.points.length - 1].y = last.points[0].y;
                        last.points[0].x = first.points[first.points.length - 1].x;
                    }
                    if (segment.length || segment.length === 0) {
                        this.findSegmentDirection(first);
                    }
                    this.findSegmentDirection(last);
                }
            }
        }
    }
    findSegmentDirection(segment) {
        if (segment.direction && (segment.length || segment.length === 0)) {
            segment.length = Point.distancePoints(segment.points[0], segment.points[segment.points.length - 1]);
            segment.direction = Point.direction(segment.points[0], segment.points[segment.points.length - 1]);
        }
    }
    removeNextSegment(connector, index) {
        const segment = this.selectedSegment;
        const first = connector.segments[index - 1];
        const last = connector.segments[index + 2];
        const next = connector.segments[index + 1];
        // eslint-disable-next-line
        let removeSegments;
        let args;
        if (next.length || next.length === 0) {
            removeSegments = connector.segments.slice(index, 2);
            args = {
                element: connector, removeSegments: removeSegments, type: 'Removal', cancel: false
            };
            args = {
                element: cloneBlazorObject(connector), removeSegments: cloneBlazorObject(removeSegments),
                type: 'Removal', cancel: false
            };
            //Removed isBlazor code
            this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
            if (!args.cancel) {
                connector.segments.splice(index, 2);
                if (segment.direction === 'Top' || segment.direction === 'Bottom') {
                    last.points[0].y = segment.points[0].y;
                    first.points[first.points.length - 1].x = last.points[0].x;
                }
                else {
                    last.points[0].x = segment.points[0].x;
                    first.points[first.points.length - 1].y = last.points[0].y;
                }
            }
        }
        else {
            removeSegments = connector.segments.slice(index + 1, 1);
            args = {
                element: connector, removeSegments: removeSegments, type: 'Removal', cancel: false
            };
            //Removed isBlazor code
            this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
            if (!args.cancel) {
                connector.segments.splice(index + 1, 1);
                if (segment.direction === 'Top' || segment.direction === 'Bottom') {
                    first.points[first.points.length - 1].x = next.points[next.points.length - 1].x;
                }
                else {
                    first.points[first.points.length - 1].y = next.points[next.points.length - 1].y;
                }
                this.findSegmentDirection(first);
                segment.length = segment.direction = null;
            }
        }
        if (first && last && !args.cancel) {
            first.length = Point.distancePoints(first.points[0], last.points[0]);
            first.direction = Point.direction(first.points[0], last.points[0]);
            if (last.length || last.length === 0) {
                last.length = Point.distancePoints(first.points[first.points.length - 1], last.points[last.points.length - 1]);
                const point1 = first.points;
                const point2 = last.points;
                last.direction = Point.direction(point1[point1.length - 1], point2[point2.length - 1]);
            }
        }
    }
    /**
     * addOrRemoveSegment method Used to add or remove intermediate segments to the straight connector. \
     *
     * @returns {void} addOrRemoveSegment method Used to add or remove intermediate segments to the straight connector.
     * @param {ConnectorModel} connector - provide the connector value in which segment to be added/removed.
     * @param {PointModel} point - provide the mouse clicked position as a point of the segment
     * @param {CommandHandler} commandHandler - provide the CommandHandler value that defines the behavior of commands
     * @private
     */
    addOrRemoveSegment(connector, point, commandHandler) {
        let updateSeg;
        let segmentIndex;
        const segmentsChanged = [];
        const oldValues = { segments: connector.segments };
        for (let i = 0; i < connector.segments.length; i++) {
            const segment = (connector.segments)[parseInt(i.toString(), 10)];
            if (contains(point, segment.point, connector.hitPadding)) {
                segmentIndex = i;
                updateSeg = true;
                //848696-Trigger SegmentCollectionChange Event for straight connector
                segmentsChanged.push(segment);
                const args = {
                    element: connector, removeSegments: segmentsChanged, type: 'Removal', cancel: false
                };
                if (commandHandler) {
                    commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
                }
            }
        }
        //827745-support to edit Segment for Straight connector at runtime
        if (!this.commandHandler) {
            this.commandHandler = commandHandler;
        }
        if (updateSeg && segmentIndex !== undefined) {
            if (connector.segments && connector.segments[parseInt(segmentIndex.toString(), 10)] && connector.segments[parseInt(segmentIndex.toString(), 10)].type === 'Straight') {
                const segment = connector.segments[parseInt(segmentIndex.toString(), 10)];
                const previous = connector.segments[segmentIndex + 1];
                if (previous) {
                    connector.segments.splice(segmentIndex, 1);
                    previous.points[0] = segment.points[0];
                }
            }
        }
        else {
            this.commandHandler.enableServerDataBinding(false);
            const index = this.findIndex(connector, point);
            if (connector.segments && connector.segments[parseInt(index.toString(), 10)] && connector.segments[parseInt(index.toString(), 10)].type === 'Straight') {
                const segment = connector.segments[parseInt(index.toString(), 10)];
                const newseg = new StraightSegment(connector, 'segments', { type: 'Straight', point: point }, true);
                newseg.points[0] = segment.points[0];
                newseg.points[1] = point;
                segment.points[0] = point;
                connector.segments.splice(index, 0, newseg);
                updateSeg = true;
                //848696-Trigger SegmentCollectionchange Event for straight connector
                segmentsChanged.push(segment);
                const args = {
                    element: connector, addSegments: segmentsChanged, type: 'Addition', cancel: false
                };
                this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
            }
            this.commandHandler.enableServerDataBinding(true);
        }
        if (updateSeg) {
            this.commandHandler.updateEndPoint(connector, oldValues);
        }
    }
    findIndex(connector, point) {
        const intersectingSegs = [];
        for (let i = 0; i < connector.segments.length; i++) {
            const segment = connector.segments[parseInt(i.toString(), 10)];
            const rect = Rect.toBounds([segment.points[0], segment.points[1]]);
            rect.Inflate(connector.hitPadding);
            if (rect.containsPoint(point)) {
                intersectingSegs.push(segment);
            }
        }
        if (intersectingSegs.length === 1) {
            return connector.segments.indexOf(intersectingSegs[0]);
        }
        else {
            let ratio;
            let min;
            let index;
            let seg;
            let v;
            let h;
            for (let i = 0; i < intersectingSegs.length; i++) {
                seg = intersectingSegs[parseInt(i.toString(), 10)];
                v = (point.y - seg.points[0].y) / (seg.points[1].y - point.y);
                h = (point.x - seg.points[0].x) / (seg.points[1].x - point.x);
                ratio = Math.abs(v - h);
                if (i === 0) {
                    min = ratio;
                    index = 0;
                }
                if (ratio < min) {
                    min = ratio;
                    index = i;
                }
            }
            return connector.segments.indexOf(intersectingSegs[parseInt(index.toString(), 10)]);
        }
    }
    dragOrthogonalSegment(obj, segment, point, segmentIndex) {
        const segmentPoint = { x: 0, y: 0 };
        const oldValues = { segments: obj.segments };
        segmentPoint.x = ((segment.points[parseInt(segmentIndex.toString(), 10)].x + segment.points[segmentIndex + 1].x) / 2);
        segmentPoint.y = ((segment.points[parseInt(segmentIndex.toString(), 10)].y + segment.points[segmentIndex + 1].y) / 2);
        const ty = point.y - segmentPoint.y;
        const tx = point.x - segmentPoint.x;
        let index = obj.segments.indexOf(segment);
        let update = false;
        //const orientation: string = (segment.points[0].y.toFixed(2) === segment.points[1].y.toFixed(2)) ? 'horizontal' : 'vertical';
        //const prevSegment: OrthogonalSegmentModel; const nextSegment: OrthogonalSegmentModel;
        // EJ2-65063 - If the allowNodeOverlap is set as true means then we set the segment index which is going to drag in selectedSegmentIndex property
        if (obj.allowNodeOverlap) {
            obj.selectedSegmentIndex = index ? index : segmentIndex;
        }
        if (index !== -1) {
            if (index === 0 && obj.segments.length === 1 && segment.points.length === 2) {
                index = this.addSegments(obj, segment, tx, ty, index);
                update = true;
            }
            else if (index === obj.segments.length - 1 && (segment.direction === null || segment.length === null)) {
                index = this.addTerminalSegment(obj, segment, tx, ty, segmentIndex);
                update = true;
            }
            else if (index === 0) {
                index = this.insertFirstSegment(obj, segment, tx, ty, index);
                update = true;
            }
            if (index) {
                if (update) {
                    this.selectedSegment = segment = obj.segments[parseInt(index.toString(), 10)];
                    this.segmentIndex = 0;
                }
                this.updateAdjacentSegments(obj, index, tx, ty);
                this.commandHandler.updateEndPoint(obj, oldValues);
            }
        }
        return true;
    }
    addSegments(obj, segment, tx, ty, coll) {
        let index;
        const segments = []; //let len: number;
        const length = Point.distancePoints(segment.points[0], segment.points[1]);
        const segmentDirection = Point.direction(segment.points[0], segment.points[1]);
        segments.push(new OrthogonalSegment(obj, 'segments', { type: 'Orthogonal', direction: segmentDirection, length: length / 4 }, true));
        const direction = (segment.points[0].y === segment.points[1].y) ? ((ty > 0) ? 'Bottom' : 'Top') : ((tx > 0) ? 'Right' : 'Left');
        const len = (segment.points[0].x === segment.points[1].x) ? ty : tx;
        segments.push(new OrthogonalSegment(obj, 'segments', { type: 'Orthogonal', direction: direction, length: len }, true));
        segments.push(new OrthogonalSegment(obj, 'segments', { type: 'Orthogonal', direction: segmentDirection, length: length / 2 }, true));
        const args = {
            element: obj, addSegments: segments, type: 'Addition', cancel: false
        };
        //Removed isBlazor code
        this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
        if (!args.cancel) {
            obj.segments = segments.concat(obj.segments);
            index = coll + 2;
        }
        return index;
    }
    // eslint-disable-next-line
    insertFirstSegment(obj, segment, tx, ty, coll) {
        let direction;
        let length;
        const segments = [];
        let segValues;
        let index;
        let insertseg;
        if (obj.sourcePortID && segment.length && obj.segments[0].points.length > 2) {
            let prev;
            for (let i = 0; i < segment.points.length - 1; i++) {
                const len = Point.distancePoints(segment.points[parseInt(i.toString(), 10)], segment.points[i + 1]);
                const dir = Point.direction(segment.points[parseInt(i.toString(), 10)], segment.points[i + 1]);
                insertseg = new OrthogonalSegment(obj, 'segments', { type: 'Orthogonal', direction: dir, length: len }, true);
                if (insertseg.length === 0) {
                    if (prev && (prev.direction === 'Top' || prev.direction === 'Bottom')) {
                        insertseg.direction = tx > 0 ? 'Right' : 'Left';
                    }
                    else {
                        insertseg.direction = ty > 0 ? 'Bottom' : 'Top';
                    }
                }
                prev = insertseg;
                segments.push(insertseg);
            }
        }
        else {
            segValues = { type: 'Orthogonal', direction: segment.direction, length: segment.length / 3 };
            segments.push(new OrthogonalSegment(obj, 'segments', segValues, true));
            if (segment.direction === 'Bottom' || segment.direction === 'Top') {
                length = Math.abs(tx);
                direction = tx > 0 ? 'Right' : 'Left';
            }
            else {
                length = Math.abs(ty);
                direction = ty > 0 ? 'Bottom' : 'Top';
            }
            insertseg = new OrthogonalSegment(obj, 'segments', { type: 'Orthogonal', direction: direction, length: length }, true);
            segments.push(insertseg);
        }
        const args = {
            element: obj, addSegments: segments, type: 'Addition', cancel: false
        };
        //Removed isBlazor code
        this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args);
        if (!args.cancel) {
            if (obj.sourcePortID && segment.length && obj.segments[0].points.length > 2) {
                obj.segments.splice(0, 1);
                index = 1;
            }
            else {
                const nextseg = obj.segments[1];
                if (nextseg && nextseg.length) {
                    nextseg.length = (direction !== nextseg.direction) ? nextseg.length + length : nextseg.length - length;
                }
                index = 2;
                segment.length = 2 * segment.length / 3;
            }
            obj.segments = segments.concat(obj.segments);
        }
        return index;
    }
    updateAdjacentSegments(obj, index, tx, ty) {
        const current = obj.segments[parseInt(index.toString(), 10)];
        const endPoint = current.points[current.points.length - 1];
        const startPoint = current.points[0];
        let isNextUpdate = true;
        if (current.type === 'Orthogonal') {
            current.points[0] = startPoint;
            current.points[current.points.length - 1] = endPoint;
            const prev = obj.segments[index - 1];
            if (prev) {
                isNextUpdate = this.updatePreviousSegment(tx, ty, obj, index);
            }
            if (obj.segments.length - 1 > index && isNextUpdate) {
                const nextSegment = obj.segments[index + 1];
                this.updateNextSegment(obj, current, nextSegment, tx, ty);
            }
        }
    }
    addTerminalSegment(connector, segment, tx, ty, segmentIndex) {
        //const oldValues: Connector = { segments: connector.segments } as Connector;
        let index = connector.segments.indexOf(segment);
        let first;
        let insertseg;
        let len;
        let dir;
        const segments = [];
        const removeSegment = connector.segments.pop();
        const last = connector.segments[connector.segments.length - 1];
        first = (last && last.type === 'Orthogonal') ? last : null;
        for (let i = 0; i < segment.points.length - 2; i++) {
            len = Point.distancePoints(segment.points[parseInt(i.toString(), 10)], segment.points[i + 1]);
            dir = Point.direction(segment.points[parseInt(i.toString(), 10)], segment.points[i + 1]);
            insertseg = new OrthogonalSegment(connector, 'segments', { type: 'Orthogonal', length: len, direction: dir }, true);
            segments.push(insertseg);
            first = insertseg;
        }
        const sec = segmentIndex;
        if (segment.points.length === 2 || sec === segment.points.length - 2) {
            if (first) {
                first.length += 5;
            }
            if (sec !== undefined) {
                //let newseg: OrthogonalSegment;
                len = 2 * Point.distancePoints(segment.points[segment.points.length - 2], segment.points[segment.points.length - 1]) / 3;
                dir = Point.direction(segment.points[segment.points.length - 2], segment.points[segment.points.length - 1]);
                const newseg = new OrthogonalSegment(connector, 'segments', { type: 'Orthogonal', length: len, direction: dir });
                segments.push(newseg);
            }
        }
        const lastseg = new OrthogonalSegment(connector, 'segments', { type: 'Orthogonal' }, true);
        segments.push(lastseg);
        const args = {
            element: connector, addSegments: segments, type: 'Addition', cancel: false
        };
        const args1 = {
            element: cloneBlazorObject(connector), addSegments: cloneBlazorObject(segments),
            type: 'Addition', cancel: args.cancel
        };
        //Removed isBlazor code
        this.commandHandler.triggerEvent(DiagramEvent.segmentCollectionChange, args1);
        if (!args1.cancel) {
            connector.segments = connector.segments.concat(segments);
            index = index + segmentIndex;
        }
        else {
            connector.segments.push(removeSegment);
        }
        this.commandHandler.updateEndPoint(connector);
        return index;
    }
    // eslint-disable-next-line
    updatePortSegment(prev, connector, index, tx, ty) {
        if (index === 1 && prev.points.length === 2 && prev.length < 0) {
            const source = connector.sourceWrapper.corners;
            const current = connector.segments[parseInt(index.toString(), 10)];
            const next = connector.segments[index + 1];
            let newseg;
            const segment = [];
            newseg = new OrthogonalSegment(connector, 'segments', { type: 'Orthogonal', length: 13, direction: prev.direction });
            segment.push(newseg);
            let len;
            if (current.direction === 'Left') {
                len = (current.points[0].x - (source.middleLeft.x - 20));
            }
            else if (current.direction === 'Right') {
                len = ((source.middleRight.x + 20) - current.points[0].x);
            }
            else if (current.direction === 'Bottom') {
                len = ((source.bottomCenter.y + 20) - current.points[0].y);
            }
            else {
                len = (current.points[0].y - (source.topCenter.y - 20));
            }
            //const dir: Direction = current.direction;
            newseg = new OrthogonalSegment(connector, 'segments', { type: 'Orthogonal', length: len, direction: current.direction });
            segment.push(newseg);
            current.length = current.length - len;
            if (next && next.length && next.direction) {
                if (next.direction === prev.direction) {
                    next.length -= 13;
                }
                else if (next.direction === getOppositeDirection(prev.direction)) {
                    next.length += 13;
                }
            }
            connector.segments = segment.concat(connector.segments);
            this.selectedSegment = connector.segments[3];
        }
    }
    updatePreviousSegment(tx, ty, connector, index) {
        const current = connector.segments[parseInt(index.toString(), 10)];
        const prev = connector.segments[index - 1];
        //const firstSegment: boolean = (index === 1) ? true : false;
        prev.points[prev.points.length - 1] = current.points[0];
        const isSourceNode = (connector.sourceID && connector.sourcePortID === '') ? false : true;
        let isNextUpdate = true;
        if (prev.type === 'Orthogonal') {
            if (prev.direction === 'Bottom') {
                prev.length += ty;
            }
            else if (prev.direction === 'Top') {
                prev.length -= ty;
            }
            else if (prev.direction === 'Right') {
                prev.length += tx;
            }
            else {
                prev.length -= tx;
            }
            // EJ2-65063 - Added the below condition !allowNodeOverlap to prevent the segment to split into two segments while intersect with source node.
            if (connector.sourcePortID !== '' && prev.length < 0 && !connector.allowNodeOverlap) {
                this.updatePortSegment(prev, connector, index, tx, ty);
            }
            else if (connector.sourceID && connector.sourcePortID === '' && prev.length < 0 && index === 1) {
                isNextUpdate = false;
                this.updateFirstSegment(connector, current);
            }
            if (isSourceNode) {
                this.changeSegmentDirection(prev, connector);
            }
        }
        return isNextUpdate;
    }
    changeSegmentDirection(segment, connector) {
        if (!connector.maxSegmentThumb) {
            if (segment.length < 0) {
                segment.direction = getOppositeDirection(segment.direction);
                segment.length *= -1;
            }
        }
    }
    updateNextSegment(obj, current, next, tx, ty) {
        next.points[0] = current.points[current.points.length - 1];
        if (next && next.type === 'Orthogonal') {
            if (next.length || next.length === 0) {
                if (next.direction === 'Left' || next.direction === 'Right') {
                    if (tx !== 0) {
                        next.length = (next.direction === 'Right') ? next.length - tx : next.length + tx;
                        if (next.length || next.length === 0) {
                            this.changeSegmentDirection(next, obj);
                        }
                    }
                }
                else {
                    if (ty !== 0) {
                        next.length = (next.direction === 'Bottom') ? next.length - ty : next.length + ty;
                        if (next.length || next.length === 0) {
                            this.changeSegmentDirection(next, obj);
                        }
                    }
                }
            }
        }
    }
    updateFirstSegment(connector, selectedSegment) {
        const index = connector.segments.indexOf(selectedSegment);
        let insertfirst = false;
        const current = connector.segments[parseInt(index.toString(), 10)];
        const prev = connector.segments[index - 1];
        const con = connector;
        let sourcePoint;
        const oldValues = { segments: connector.segments };
        if (prev.length < 0 && connector.sourceID) {
            const sourceNode = connector.sourceWrapper.corners;
            const segments = [];
            let segValues;
            let removeCurrentPrev = false;
            this.changeSegmentDirection(current, connector);
            const next = connector.segments[index + 1];
            const nextNext = connector.segments[index + 2];
            if (next) {
                this.changeSegmentDirection(next, connector);
            }
            if (nextNext) {
                this.changeSegmentDirection(nextNext, connector);
            }
            switch (prev.direction) {
                case 'Top':
                case 'Bottom':
                    sourcePoint = (current.length > 0 && current.direction === 'Left') ? sourceNode.middleLeft : sourceNode.middleRight;
                    if (current.length > sourceNode.width / 2) {
                        if (Math.abs(prev.length) < sourceNode.height / 2) {
                            prev.length = Point.distancePoints(sourceNode.center, prev.points[prev.points.length - 1]);
                            current.points[0].x = sourcePoint.x;
                            current.length = Point.distancePoints(current.points[0], current.points[current.points.length - 1]);
                            current.length -= 20;
                            insertfirst = true;
                        }
                    }
                    else {
                        if (next && next.direction && next.length) {
                            next.points[0].y = sourcePoint.y;
                            next.points[0].x = next.points[next.points.length - 1].x = (current.direction === 'Right') ?
                                sourcePoint.x + 20 : sourcePoint.x - 20;
                        }
                        insertfirst = true;
                        removeCurrentPrev = true;
                    }
                    break;
                case 'Left':
                case 'Right':
                    sourcePoint = (current.length > 0 && current.direction === 'Top') ? sourceNode.topCenter : sourceNode.bottomCenter;
                    if (current.length > sourceNode.height / 2) {
                        if (Math.abs(prev.length) < sourceNode.width / 2) {
                            prev.length = Point.distancePoints(sourceNode.center, prev.points[prev.points.length - 1]);
                            current.points[0].y = sourcePoint.y;
                            current.length = Point.distancePoints(current.points[0], current.points[current.points.length - 1]);
                            current.length -= 20;
                            insertfirst = true;
                        }
                    }
                    else {
                        if (next && next.direction && next.length) {
                            next.points[0].x = sourcePoint.x;
                            next.points[0].y = next.points[next.points.length - 1].y = (current.direction === 'Bottom') ?
                                sourcePoint.y + 20 : sourcePoint.y - 20;
                        }
                        insertfirst = true;
                        removeCurrentPrev = true;
                    }
                    break;
            }
            this.changeSegmentDirection(prev, connector);
            this.changeSegmentDirection(current, connector);
            if (insertfirst) {
                segValues = { type: 'Orthogonal', direction: current.direction, length: 20 };
                segments.push(new OrthogonalSegment(connector, 'segments', segValues, true));
                if (removeCurrentPrev) {
                    if (next && next.direction && next.length) {
                        next.length = Point.distancePoints(next.points[0], next.points[next.points.length - 1]);
                    }
                    if (nextNext && nextNext.direction && nextNext.length) {
                        nextNext.length = Point.distancePoints(next.points[next.points.length - 1], nextNext.points[nextNext.points.length - 1]);
                    }
                    connector.segments.splice(index - 1, 2);
                }
                connector.segments = segments.concat(connector.segments);
            }
            this.selectedSegment = ((removeCurrentPrev) ? connector.segments[index - 1] :
                connector.segments[index + 1]);
            this.commandHandler.updateEndPoint(connector, oldValues);
        }
    }
    updateLastSegment(connector, selectedSegment) {
        if (connector.targetID && connector.targetPortID === '') {
            let line1Start;
            let line1End;
            let line2Start;
            let line2End;
            const corners = connector.targetWrapper.corners;
            const firstSegPoint = selectedSegment.points[0];
            const lastSegPoint = selectedSegment.points[selectedSegment.points.length - 1];
            if (selectedSegment.direction === 'Right' || selectedSegment.direction === 'Left') {
                line1Start = { x: firstSegPoint.x, y: firstSegPoint.y };
                line1End = {
                    x: (selectedSegment.direction === 'Left') ? lastSegPoint.x - corners.width / 2 : lastSegPoint.x + corners.width / 2,
                    y: lastSegPoint.y
                };
                line2Start = { x: corners.center.x, y: corners.center.y - corners.height };
                line2End = { x: corners.center.x, y: corners.center.y + corners.height };
            }
            else {
                line1Start = { x: firstSegPoint.x, y: firstSegPoint.y };
                line1End = {
                    x: lastSegPoint.x,
                    y: (selectedSegment.direction === 'Bottom') ? lastSegPoint.y + corners.height / 2 : lastSegPoint.y - corners.height / 2
                };
                line2Start = { x: corners.center.x - corners.width, y: corners.center.y };
                line2End = { x: corners.center.x + corners.width, y: corners.center.y };
            }
            const line1 = { x1: line1Start.x, y1: line1Start.y, x2: line1End.x, y2: line1End.y };
            const line2 = { x1: line2Start.x, y1: line2Start.y, x2: line2End.x, y2: line2End.y };
            return (intersect3(line1, line2).enabled);
        }
        return false;
    }
    /**
     *To destroy the module
     *
     * @returns {void} To destroy the module
     */
    destroy() {
        /**
         * Destroys the connector editing module
         */
    }
    /**
     * Get module name.
     */
    /**
     * Get module name.\
     *
     * @returns {  string  }    Get module name.\
     */
    getModuleName() {
        /**
         * Returns the module name
         */
        return 'ConnectorEditingTool';
    }
}
